import { useState, useEffect } from 'react';

// Custom hook to observe localStorage changes
function useLocalStorageObserver(key: string) {
  const [value, setValue] = useState(() => localStorage.getItem(key));

  const updateValue = () => {
    setValue(localStorage.getItem(key)); // Re-fetch the value from localStorage
  };

  useEffect(() => {
    // Listen for storage changes across tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key) {
        setValue(event.newValue); // Update the value when localStorage is changed in another tab
      }
    };

    // Bind storage event listener
    window.addEventListener('storage', handleStorageChange);

    // Override localStorage.setItem to ensure we manually update the state in the same tab
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function (...args) {
      originalSetItem.apply(this, args); // Perform the original setItem operation
      if (args[0] === key) {
        updateValue(); // Update state in the same tab when localStorage value changes
      }
    };

    // Cleanup listener and restore original setItem on component unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      localStorage.setItem = originalSetItem; // Restore original setItem function
    };
  }, [key]); // Dependency on key to re-run effect when key changes

  return value;
}

export default useLocalStorageObserver;
